import EmailOptInConfirmation from '@/components/notifications/confirmation';
import { useRouter } from 'next/router';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

type EmailOptInConfirmationContextValue = {
    isDialogOpen: boolean;
};

const EmailOptInConfirmationContext =
    createContext<EmailOptInConfirmationContextValue>({
        isDialogOpen: false,
    });

export const useEmailOptInConfirmation = () =>
    useContext(EmailOptInConfirmationContext);

const DEEPLINK_VALUE_PARAM_CONFIRMATION_EMAIL_COMMUNICATION =
    'confirm-email-communication';

export function EmailOptInConfirmationProvider({
    children,
}: PropsWithChildren) {
    const { query, replace, isReady } = useRouter();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState<string>();

    const hasProcessedDeepLinkRef = useRef(false);

    useEffect(() => {
        if (!isReady || hasProcessedDeepLinkRef.current) {
            return;
        }

        const confirmationCodeParam = query.deep_link_sub1;
        const deepLinkValueParam = query.deep_link_value;

        const confirmationCode = Array.isArray(confirmationCodeParam)
            ? confirmationCodeParam[0]
            : confirmationCodeParam;

        const deepLinkValue = Array.isArray(deepLinkValueParam)
            ? deepLinkValueParam[0]
            : deepLinkValueParam;

        hasProcessedDeepLinkRef.current = true;

        // Only process if deep_link_value is specifically for email confirmation
        if (
            deepLinkValue !==
            DEEPLINK_VALUE_PARAM_CONFIRMATION_EMAIL_COMMUNICATION
        ) {
            return;
        }

        setConfirmationCode(confirmationCode);
        setIsDialogOpen(true);
    }, [isReady, query.deep_link_value, query.deep_link_sub1, replace]);

    const handleClose = useCallback(() => {
        setIsDialogOpen(false);
        setConfirmationCode(undefined);
    }, []);

    return (
        <EmailOptInConfirmationContext.Provider
            value={{
                isDialogOpen,
            }}
        >
            {children}
            {isDialogOpen && confirmationCode ? (
                <EmailOptInConfirmation
                    confirmationCode={confirmationCode}
                    onClose={handleClose}
                />
            ) : null}
        </EmailOptInConfirmationContext.Provider>
    );
}
